import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Sucursales = () => {
  const { user, loading: authLoading } = useAuth();
  const [sucursales, setSucursales] = useState([]);
  const [sucursalesFiltrados, setSucursalesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [limiteSucursales, setLimiteSucursales] = useState(null);
  const [tieneMultiplesSucursales, setTieneMultiplesSucursales] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    es_principal: false,
    activo: true
  });

  // Estilos comunes para inputs
  const inputStyle = {
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#fafafa',
    width: '100%',
    transition: 'all 0.2s'
  };

  // Estilos para el mensaje de acceso denegado
  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    },
    card: {
      backgroundColor: 'white',
      padding: '2rem 3rem',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      textAlign: 'center',
      maxWidth: '500px',
    },
    icon: {
      fontSize: '4rem',
      display: 'block',
      marginBottom: '1rem',
    },
    message: {
      color: '#6b7280',
      marginBottom: '1.5rem',
      lineHeight: '1.6',
    },
    suggestion: {
      fontSize: '0.9rem',
      color: '#9ca3af',
      marginBottom: '2rem',
    },
    button: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
  };

  useEffect(() => {
    if (!authLoading) {
      if (user?.tenant?.acceso_bloqueado === true) {
        setAccesoDenegado(true);
        setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
        setLoading(false);
        return;
      }
      if (user?.rol !== 'admin') {
        setAccesoDenegado(true);
        setMensajeError('Solo los administradores pueden gestionar sucursales.');
        setLoading(false);
        return;
      }
      fetchInfoPlan();
    }
  }, [authLoading]);

  useEffect(() => {
    if (!authLoading && limiteSucursales !== null) {
      fetchSucursales();
    }
  }, [authLoading, limiteSucursales]);

  useEffect(() => {
    // Filtrar sucursales cuando cambia el término de búsqueda o la lista
    if (sucursales.length > 0) {
      const filtrados = sucursales.filter(s =>
        s.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.direccion && s.direccion.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.telefono && s.telefono.includes(searchTerm))
      );
      setSucursalesFiltrados(filtrados);
    } else {
      setSucursalesFiltrados([]);
    }
  }, [searchTerm, sucursales]);

  const fetchInfoPlan = async () => {
    try {
      const { data } = await api.get('/tenant/plan-info');
      
      // Verificar si tiene múltiples sucursales
      // Si max_sucursales es 1 o no existe, solo permite una
      const maxSucursales = data.plan?.max_sucursales || 1;
      const multiples = maxSucursales > 1;
      
      setLimiteSucursales(maxSucursales);
      setTieneMultiplesSucursales(multiples);
      
    } catch {
      setLimiteSucursales(1);
      setTieneMultiplesSucursales(false);
    }
  };

  const fetchSucursales = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/sucursales');
      setSucursales(data);
      setSucursalesFiltrados(data);
      setAccesoDenegado(false);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setAccesoDenegado(true);
        setMensajeError(error.response.data.message || 'Tu plan no incluye múltiples sucursales.');
      } else {
        toast.error(error?.response?.data?.error || 'Error al cargar sucursales');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar límite de sucursales
    if (!editingId && !tieneMultiplesSucursales && sucursales.length >= 1) {
      toast.error('❌ Tu plan solo permite una sucursal. Ya tienes una creada.');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/sucursales/${editingId}`, formData);
        toast.success('Sucursal actualizada');
      } else {
        await api.post('/sucursales', formData);
        toast.success('Sucursal creada');
      }
      fetchSucursales();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar sucursal');
    }
  };

  const handleEdit = (sucursal) => {
    setFormData({
      nombre: sucursal.nombre,
      direccion: sucursal.direccion || '',
      telefono: sucursal.telefono || '',
      email: sucursal.email || '',
      es_principal: sucursal.es_principal,
      activo: sucursal.activo
    });
    setEditingId(sucursal.id);
    setShowModal(true);
  };

  const handleDelete = async (id, esPrincipal) => {
    if (esPrincipal) {
      toast.error('No se puede eliminar la sucursal principal');
      return;
    }

    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/sucursales/${id}`);
        toast.success('Sucursal eliminada');
        fetchSucursales();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    }});
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      nombre: '',
      direccion: '',
      telefono: '',
      email: '',
      es_principal: false,
      activo: true
    });
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="loading"></div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
            Cargando sucursales...
          </p>
        </div>
      </Layout>
    );
  }

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.card}>
            <span style={styles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={styles.message}>{mensajeError}</p>
            <p style={styles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button 
              style={styles.button} 
              onClick={() => navigate('/dashboard')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h2>🏢 Sucursales</h2>
          {!tieneMultiplesSucursales && (
            <div style={{ 
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: '#6b7280',
              background: '#f3f4f6',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              display: 'inline-block'
            }}>
              📊 Plan básico: 1 sucursal permitida
            </div>
          )}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowModal(true)}
          disabled={!tieneMultiplesSucursales && sucursales.length >= 1}
          title={!tieneMultiplesSucursales && sucursales.length >= 1 ? 'Tu plan solo permite una sucursal' : ''}
        >
          + Nueva Sucursal
        </button>
      </div>

      {/* Buscador */}
      <div className="card mb-3">
        <input
          type="text"
          className="form-input"
          placeholder="🔍 Buscar sucursal por nombre, dirección o teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }}
        />
        {searchTerm && (
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.875rem', 
            color: 'var(--text-secondary)',
            textAlign: 'right'
          }}>
            {sucursalesFiltrados.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      {sucursalesFiltrados.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <span style={{ fontSize: '3rem' }}>🏢</span>
          <h3 style={{ marginTop: '1rem' }}>
            {searchTerm ? 'No hay resultados para tu búsqueda' : 'No hay sucursales creadas'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            {searchTerm 
              ? 'Prueba con otros términos de búsqueda' 
              : 'Crea tu primera sucursal haciendo click en el botón "+ Nueva Sucursal"'}
          </p>
        </div>
      ) : (
        <div className="grid grid-3">
          {sucursalesFiltrados.map(sucursal => (
            <div key={sucursal.id} className="card">
              <div className="flex justify-between items-center mb-2">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {sucursal.nombre}
                  {sucursal.es_principal && (
                    <span className="badge badge-success ml-2">Principal</span>
                  )}
                </h3>
                {!sucursal.activo && (
                  <span className="badge badge-danger">Inactiva</span>
                )}
              </div>
              
              <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {sucursal.direccion && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    📍 {sucursal.direccion}
                  </p>
                )}
                {sucursal.telefono && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    📞 {sucursal.telefono}
                  </p>
                )}
                {sucursal.email && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    ✉️ {sucursal.email}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <button 
                  className="btn btn-sm btn-secondary" 
                  onClick={() => handleEdit(sucursal)}
                >
                  ✏️ Editar
                </button>
                {!sucursal.es_principal && (
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => handleDelete(sucursal.id, sucursal.es_principal)}
                  >
                    🗑️ Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  🏢
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? 'Modifica la información de la sucursal' : 'Completa los datos para agregar una nueva sucursal'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {!tieneMultiplesSucursales && sucursales.length >= 1 && !editingId && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fee', border: '1px solid #dc2626', borderRadius: '8px', color: '#dc2626', fontSize: '0.875rem' }}>
                    ⚠️ Tu plan solo permite una sucursal. Ya tienes una creada.
                  </div>
                )}

                {/* Información de la sucursal */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>🏢 Información de la sucursal</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombre',    label: 'Nombre',    required: true, placeholder: 'Ej. Sucursal Norte',    full: true },
                      { key: 'direccion', label: 'Dirección',                 placeholder: 'Calle, número, sector', full: true },
                      { key: 'telefono',  label: 'Teléfono',  type: 'tel',    placeholder: '0999 999 999' },
                      { key: 'email',     label: 'Email',     type: 'email',  placeholder: 'sucursal@ejemplo.com' },
                    ].map(({ key, label, required, type, placeholder, full }) => (
                      <div key={key} className="form-group" style={full ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input type={type || 'text'} className="form-input" value={formData[key]}
                          onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                          required={!!required} placeholder={placeholder} style={inputStyle}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Configuración */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>⚙️ Configuración</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      <input type="checkbox" checked={formData.es_principal}
                        onChange={(e) => setFormData({...formData, es_principal: e.target.checked})}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                      Marcar como sucursal principal
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                      <input type="checkbox" checked={formData.activo}
                        onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                      Sucursal activa
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Guardar cambios' : '+ Crear sucursal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Sucursales;
